import React from "react";
import moment from "moment";
import { formatMoney } from "../../../features/utils/formatMoney";

export function money(amount) {
  if (amount === null || amount === undefined || amount === "") return "-";
  return formatMoney(amount);
}

export function formatDate(value, format = "DD MMM YYYY") {
  if (!value) return "-";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(format) : value;
}

export function getRouteSummary(flight) {
  const destinations = flight?.destinations || [];
  if (destinations.length) {
    return destinations.map((leg) => `${leg.from} → ${leg.to}`).join(" · ");
  }
  const flights = flight?.flights || [];
  if (!flights.length) return "-";
  const first = flights[0];
  const last = flights[flights.length - 1];
  return `${first?.departureLocation || "?"} → ${last?.arrivalLocation || "?"}`;
}

export function getPassengerSummary(travelers = []) {
  if (!travelers.length) return "-";
  const counts = travelers.reduce((acc, traveler) => {
    const type = traveler?.travelerType || "PAX";
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts)
    .map(([type, count]) => `${count} ${type}`)
    .join(", ");
}

export function maskPassport(number) {
  const value = String(number || "");
  if (value.length <= 3) return value || "-";
  return `${"*".repeat(Math.max(value.length - 3, 8))}${value.slice(-3)}`;
}

export function maskEmail(email) {
  const value = String(email || "");
  const [name, domain] = value.split("@");
  if (!name || !domain) return value || "-";
  return `${name.slice(0, 3)}${"•".repeat(Math.max(name.length - 3, 4))}@${domain}`;
}

export function maskPhone(phone) {
  const value = String(phone || "");
  if (value.length < 6) return value || "-";
  return `${value.slice(0, 4)} ••• ••• ${value.slice(-4)}`;
}

export function getPaidInstallmentCount(
  repayments = [],
  madeRepayments = [],
  fullyPaid,
) {
  if (!repayments.length) return 0;
  if (fullyPaid) return repayments.length;

  let paidPool = madeRepayments.reduce(
    (sum, payment) => sum + Number(payment?.amount || 0),
    0,
  );

  let count = 0;
  for (const installment of repayments) {
    const amount = Number(installment?.amount || 0);
    if (paidPool + 0.01 >= amount) {
      paidPool -= amount;
      count += 1;
    } else {
      break;
    }
  }
  return count;
}

export function isFullyPaidFreeze(freeze, detail) {
  const status = String(freeze?.status || "").toUpperCase();
  return (
    status === "FULLYPAID" ||
    status === "COMPLETED" ||
    status === "PAID" ||
    Number(detail?.remainingAmount) === 0
  );
}

/** Section heading row for PDF tables */
export function SectionTitle({ children }) {
  return (
    <table className="w-full mb-2" cellPadding="0" cellSpacing="0">
      <tbody>
        <tr>
          <td className="pt-4 pb-2 border-b-2 border-gray-800">
            <span className="text-xs font-bold tracking-wider uppercase text-gray-800">
              {children}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/** Two-column key/value table rows (Adobe-friendly) */
export function InfoTable({ rows = [] }) {
  const visible = rows.filter((row) => row && !row.hide);
  if (!visible.length) return null;

  return (
    <table
      className="w-full mb-4 text-xs border border-gray-200"
      cellPadding="0"
      cellSpacing="0"
    >
      <tbody>
        {visible.map((row, index) => (
          <tr
            key={`${row.label}-${index}`}
            className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
          >
            <td className="w-2/5 px-3 py-2 border-b border-gray-200 text-gray-500 font-medium align-top">
              {row.label}
            </td>
            <td className="w-3/5 px-3 py-2 border-b border-gray-200 text-gray-800 font-semibold text-right align-top">
              {row.value ?? "-"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Empty / muted note */
export function EmptyNote({ children }) {
  return (
    <table className="w-full mb-4" cellPadding="0" cellSpacing="0">
      <tbody>
        <tr>
          <td className="px-3 py-3 text-xs text-gray-500 border border-dashed border-gray-300">
            {children}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/** Status banner */
export function StatusBanner({ tone = "warning", children }) {
  const toneClass =
    tone === "success"
      ? "bg-green-50 text-green-800 border-green-200"
      : "bg-yellow-50 text-yellow-800 border-yellow-200";

  return (
    <table className="w-full mb-3" cellPadding="0" cellSpacing="0">
      <tbody>
        <tr>
          <td
            className={`px-3 py-2 text-xs font-bold tracking-wide uppercase border ${toneClass}`}
          >
            {children}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/** Small subheading above a nested table */
export function SubTitle({ children }) {
  return (
    <table className="w-full mb-1" cellPadding="0" cellSpacing="0">
      <tbody>
        <tr>
          <td className="pt-2 pb-1">
            <span className="text-xs font-bold text-gray-700">{children}</span>
          </td>
        </tr>
      </tbody>
    </table>
  );
}
